import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Users, Hash, Copy, Check } from "lucide-react";

export default function CompetitionsList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "competition_admin" || user?.role === "super_admin";
  const [comps, setComps] = useState([]);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [form, setForm] = useState({ name: "", season: "2025/26", description: "", monthly_enabled: true, joker_enabled: true });
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");

  const refresh = async () => {
    const { data } = await api.get("/competitions");
    setComps(data);
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/competitions", form);
      setCreating(false);
      setForm({ name: "", season: "2025/26", description: "", monthly_enabled: true, joker_enabled: true });
      await refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const join = async (e) => {
    e.preventDefault(); setErr("");
    try {
      await api.post("/competitions/join", { invite_code: code });
      setJoining(false); setCode("");
      await refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const copyCode = async (c) => {
    await navigator.clipboard.writeText(c);
    setCopied(c);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="fade-up space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C7F500] font-semibold">Your competitions</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">Competitions</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setJoining(true)} data-testid="join-comp-btn"
            className="px-4 py-2.5 rounded-lg bg-[#132A45] hover:bg-[#1B4276] text-sm font-medium">
            Join with code
          </button>
          {isAdmin && (
            <button onClick={() => setCreating(true)} data-testid="create-comp-btn"
              className="px-4 py-2.5 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27]  text-[#071426] text-sm font-semibold inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> New competition
            </button>
          )}
        </div>
      </div>

      {comps.length === 0 ? (
        <div className="p-8 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] text-center">
          <p className="text-[#AAB7C7]">No competitions yet. {isAdmin ? "Create one above or" : ""} join with an invite code (try <span className="text-[#C7F500]">DEMO2026</span>).</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {comps.map((c) => (
            <Link key={c.id} to={`/app/competitions/${c.id}`} data-testid={`comp-card-${c.id}`}
              className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C] hover:border-[#C7F500]/40 transition-all">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading text-lg font-semibold">{c.name}</h3>
                  <p className="text-xs text-[#AAB7C7] mt-0.5">{c.season}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-md ${c.status === "active" ? "bg-[#C7F500]/10 text-[#C7F500]" : "bg-[#132A45] text-[#AAB7C7]"}`}>{c.status}</span>
              </div>
              {c.description && <p className="text-sm text-[#AAB7C7] line-clamp-2 mb-3">{c.description}</p>}
              <div className="flex items-center justify-between text-xs text-[#AAB7C7]">
                <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.member_count} members</span>
                <button
                  onClick={(e) => { e.preventDefault(); copyCode(c.invite_code); }}
                  data-testid={`copy-invite-${c.id}`}
                  className="inline-flex items-center gap-1 hover:text-white"
                >
                  <Hash className="w-3.5 h-3.5" /> {c.invite_code}
                  {copied === c.invite_code ? <Check className="w-3 h-3 text-[#C7F500]" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="w-full max-w-md bg-[#0D2038] border border-[#1F3A5C] rounded-2xl p-6 fade-up">
            <h2 className="font-heading text-xl font-bold mb-4">New competition</h2>
            <div className="space-y-3">
              <input required placeholder="Competition name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="new-comp-name" className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
              <input required placeholder="Season (e.g. 2025/26)" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })}
                data-testid="new-comp-season" className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
              <textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="new-comp-description" rows={3} className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5" />
              <label className="flex items-center gap-2 text-sm text-[#AAB7C7] cursor-pointer">
                <input type="checkbox" checked={form.monthly_enabled} onChange={(e) => setForm({ ...form, monthly_enabled: e.target.checked })}
                  data-testid="new-comp-monthly-toggle" className="w-4 h-4 accent-[#C7F500]" />
                Include a monthly table (you can change this later)
              </label>
              <label className="flex items-center gap-2 text-sm text-[#AAB7C7] cursor-pointer">
                <input type="checkbox" checked={form.joker_enabled} onChange={(e) => setForm({ ...form, joker_enabled: e.target.checked })}
                  data-testid="new-comp-joker-toggle" className="w-4 h-4 accent-[#C7F500]" />
                Use Jokers (one per gameweek, doubles points)
              </label>
              {err && <div className="text-sm text-red-400">{err}</div>}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276]">Cancel</button>
              <button type="submit" data-testid="new-comp-submit" className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27]  text-[#071426] font-semibold">Create</button>
            </div>
          </form>
        </div>
      )}

      {/* Join modal */}
      {joining && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setJoining(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={join} className="w-full max-w-sm bg-[#0D2038] border border-[#1F3A5C] rounded-2xl p-6 fade-up">
            <h2 className="font-heading text-xl font-bold mb-4">Join a competition</h2>
            <input required placeholder="Invite code" value={code} onChange={(e) => setCode(e.target.value)}
              data-testid="join-code-input" className="w-full bg-[#071426] border border-[#1F3A5C] focus:border-[#C7F500] focus:outline-none rounded-lg px-3 py-2.5 uppercase" />
            {err && <div className="mt-2 text-sm text-red-400">{err}</div>}
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setJoining(false)} className="px-4 py-2 rounded-lg bg-[#132A45] hover:bg-[#1B4276]">Cancel</button>
              <button type="submit" data-testid="join-submit" className="px-4 py-2 rounded-lg bg-[#C7F500] hover:bg-[#D9FF27] font-semibold">Join</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
